import { useState, useEffect, useRef } from "react";

const TagDropdown = ({ allTags, selectedCompanies, companiesData, onAddTags, onRemoveTags, onClose, onTagCreated }) => {
    const [selectedTags, setSelectedTags] = useState([]);
    const [showCreateTag, setShowCreateTag] = useState(false);
    const [newTagName, setNewTagName] = useState("");
    const [isCreating, setIsCreating] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const dropdownRef = useRef(null);

    const filteredTags = allTags.filter(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));

    const getCommonTags = () => {
        if (companiesData.length === 0) return [];
        const firstCompanyTags = companiesData[0]?.tags || [];
        return firstCompanyTags.filter(tag => companiesData.every(company => company.tags?.includes(tag)));
    };

    const commonTags = getCommonTags();

    useEffect(() => {
        setSelectedTags(commonTags);
    }, [companiesData]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) onClose();
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [onClose]);

    const handleTagToggle = (tag) => {
        setSelectedTags(prev => {
            if (prev.includes(tag)) return prev.filter(t => t !== tag);
            return [...prev, tag];
        });
    };

    const handleCreateTag = async () => {
        if (!newTagName.trim()) return;
        setIsCreating(true);
        try {
            await onAddTags([newTagName.trim()], true);
            setNewTagName("");
            setShowCreateTag(false);
            onTagCreated();
        } catch (error) {
            console.error("Error creating tag:", error);
        }
        setIsCreating(false);
    };

    const handleApply = () => {
        const tagsToAdd = selectedTags.filter(tag => !commonTags.includes(tag));
        const tagsToRemove = commonTags.filter(tag => !selectedTags.includes(tag));
        if (tagsToAdd.length > 0) onAddTags(tagsToAdd);
        if (tagsToRemove.length > 0) onRemoveTags(tagsToRemove);
        if (tagsToAdd.length === 0 && tagsToRemove.length === 0) onClose();
    };

    if (showCreateTag) {
        return (
            <div ref={dropdownRef} style={{ position: "absolute", top: "100%", right: 0, marginTop: "8px", backgroundColor: "white", borderRadius: "8px", boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)", minWidth: "280px", zIndex: 1000, border: "1px solid #e5e7eb" }}>
                <div style={{ padding: "12px 16px", borderBottom: "1px solid #e5e7eb", display: "flex", alignItems: "center", gap: "8px" }}>
                    <button onClick={() => setShowCreateTag(false)} style={{ background: "none", border: "none", cursor: "pointer", padding: "4px", color: "#6b7280", display: "flex", alignItems: "center" }}></button>
                    <span style={{ fontWeight: 600, color: "#374151" }}>Create New Tag</span>
                </div>
                <div style={{ padding: "16px" }}>
                    <input type="text" placeholder="Enter tag name..." value={newTagName} onChange={(e) => setNewTagName(e.target.value)} onKeyPress={(e) => e.key === "Enter" && handleCreateTag()} autoFocus style={{ width: "100%", padding: "10px 12px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "14px", outline: "none", marginBottom: "12px" }} />
                    <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
                        <button onClick={() => { setShowCreateTag(false); setNewTagName(""); }} style={{ padding: "8px 16px", backgroundColor: "#f3f4f6", color: "#374151", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "14px", fontWeight: 500 }}>Cancel</button>
                        <button onClick={handleCreateTag} disabled={!newTagName.trim() || isCreating} style={{ padding: "8px 16px", backgroundColor: newTagName.trim() ? "#fc7785" : "#e5e7eb", color: newTagName.trim() ? "white" : "#9ca3af", border: "none", borderRadius: "6px", cursor: newTagName.trim() ? "pointer" : "not-allowed", fontSize: "14px", fontWeight: 500 }}>{isCreating ? "Creating..." : "Create Tag"}</button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div ref={dropdownRef} style={{ position: "absolute", top: "100%", right: 0, marginTop: "8px", backgroundColor: "white", borderRadius: "8px", boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)", minWidth: "280px", zIndex: 1000, border: "1px solid #e5e7eb" }}>
            <div style={{ padding: "12px 16px", borderBottom: "1px solid #e5e7eb", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontWeight: 600, color: "#374151" }}>Manage Tags</span>
                <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", padding: "4px", color: "#374151", fontSize: "16px" }}>✕</button>
            </div>

            {allTags.length > 0 && (
                <div style={{ padding: "8px 16px", borderBottom: "1px solid #e5e7eb" }}>
                    <input type="text" placeholder="Search tags..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} style={{ width: "100%", padding: "8px 12px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "14px", outline: "none" }} />
                </div>
            )}

            <div style={{ maxHeight: "200px", overflowY: "auto", padding: "8px 0" }}>
                {allTags.length === 0 ? (
                    <div style={{ padding: "16px", textAlign: "center", color: "#9ca3af", fontSize: "14px" }}>No tags yet. Create one below.</div>
                ) : filteredTags.length === 0 ? (
                    <div style={{ padding: "16px", textAlign: "center", color: "#9ca3af", fontSize: "14px" }}>No tags found.</div>
                ) : (
                    filteredTags.map((tag, index) => {
                        const isSelected = selectedTags.includes(tag);
                        return (
                            <div key={index} onClick={() => handleTagToggle(tag)} style={{ padding: "8px 16px", display: "flex", alignItems: "center", cursor: "pointer", backgroundColor: isSelected ? "#f0f9ff" : "transparent", transition: "background-color 0.15s" }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = isSelected ? "#f0f9ff" : "#f9fafb"} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = isSelected ? "#f0f9ff" : "transparent"}>
                                <div style={{ width: "18px", height: "18px", borderRadius: "4px", border: `2px solid ${isSelected ? "#3b82f6" : "#d1d5db"}`, backgroundColor: isSelected ? "#3b82f6" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", marginRight: "12px", transition: "all 0.15s" }}>
                                    {isSelected && <span style={{ color: "white", fontSize: "12px", fontWeight: "bold", lineHeight: 1 }}>✓</span>}
                                </div>
                                <span style={{ color: "#374151", fontSize: "14px" }}>{tag}</span>
                            </div>
                        );
                    })
                )}
            </div>

            <div style={{ padding: "8px 16px", borderTop: "1px solid #e5e7eb" }}>
                <button onClick={() => setShowCreateTag(true)} style={{ width: "100%", padding: "10px 12px", backgroundColor: "#fc7785", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "14px", fontWeight: 500, display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", transition: "all 0.15s" }} onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#e5636f"; }} onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "#fc7785"; }}>Add New Tag</button>
            </div>

            <div style={{ padding: "12px 16px", borderTop: "1px solid #e5e7eb", display: "flex", gap: "8px", justifyContent: "flex-end" }}>
                <button onClick={onClose} style={{ padding: "8px 16px", backgroundColor: "#f3f4f6", color: "#374151", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "14px", fontWeight: 500 }}>Cancel</button>
                <button onClick={handleApply} style={{ padding: "8px 16px", backgroundColor: "#fc7785", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "14px", fontWeight: 500 }}>Apply</button>
            </div>
        </div>
    );
};

export default TagDropdown;
